import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { Breadcrumbs } from './ComponentLibrary';
import SellerProfileFull from './SellerProfile/SellerProfileFull';
import SeoHead from './SeoHead';
import { API_BASE } from '../config';
import { placeholderImage } from '../propertyConfig';
import './SellerProfilePage.css';

/**
 * Seller / Contributor Profile Page
 *
 * One profile per person at /seller/:id - shows property listings +
 * reviews (via SellerProfileFull, unchanged) and, if this person is also
 * an approved Property Guide contributor, an "Articles" tab. Replaces the
 * separate /blog/author/:id contributor-only page (now a redirect here)
 * so a seller who also contributes doesn't have two disconnected profiles.
 *
 * Public - no login required, matching optionalAuth on GET /sellers/:id.
 * Contributor articles are explicitly public content (see spec: "View
 * contributor profiles" is a Public permission), so this page must never
 * gate on being logged in.
 */
const SellerProfilePage = () => {
  const { sellerId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const authToken = localStorage.getItem('naijanest_token');

  const [seller, setSeller] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reviews, setReviews] = useState([]);

  const breadcrumbItems = useMemo(() => {
    const params = new URLSearchParams(location.search || '');
    const propertyId = params.get('propertyId');
    const propertyTitle = params.get('propertyTitle');

    if (propertyId) {
      return [
        {
          label: propertyTitle || 'Property',
          href: `/property/${propertyId}`,
        },
        { label: 'Seller Profile', current: true },
      ];
    }

    return [
      { label: 'Browse', href: '/browse' },
      { label: 'Seller Profile', current: true },
    ];
  }, [location.search]);

  // Fetch seller profile from API
  useEffect(() => {
    const fetchSellerProfile = async () => {
      try {
        setLoading(true);

        const profileUrl = `${API_BASE}/sellers/${sellerId}`;

        const response = await fetch(profileUrl, {
          headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
        });

        if (!response.ok) {
          throw new Error(`Seller not found (${response.status})`);
        }

        const data = await response.json();
        setSeller(data);

        // Fetch reviews for this seller
        try {
          const reviewsUrl = `${API_BASE}/sellers/${sellerId}/reviews?limit=50`;

          const reviewsResponse = await fetch(reviewsUrl);

          if (reviewsResponse.ok) {
            const reviewsData = await reviewsResponse.json();
            setReviews(reviewsData.reviews || []);
          }
        } catch (reviewErr) {
          // Reviews are optional, continue without them
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (sellerId) {
      fetchSellerProfile();
    }
  }, [authToken, sellerId]);

  // Loading state
  if (loading) {
    return (
      <div className="seller-profile-page loading">
        <div className="loading-spinner">Loading profile...</div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="seller-profile-page error">
        <SeoHead title="Profile not found" description="This seller profile may have been removed or the link is invalid." noIndex />
        <div className="error-message">
          <h2>Unable to Load Profile</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/browse')}>Browse Listings</button>
        </div>
      </div>
    );
  }

  // Not found state
  if (!seller) {
    return (
      <div className="seller-profile-page not-found">
        <SeoHead title="Profile not found" description="This seller profile may have been removed or the link is invalid." noIndex />
        <h2>Profile not found</h2>
        <button onClick={() => navigate('/browse')}>Back to Listings</button>
      </div>
    );
  }

  // Transform API seller data to match SellerProfileFull component format
  const sellerData = {
    id: seller.id,
    name: seller.name,
    avatar: seller.avatar_url || null,
    rating: seller.rating || 0,
    reviewCount: seller.total_reviews || seller.review_count || 0,
    recommendPercent:
      seller.recommend_percent === null || seller.recommend_percent === undefined
        ? null
        : Number(seller.recommend_percent),
    // Distinct from operating_territory (a property-page concept - the
    // area an agent serves, shown alongside their listings): this is the
    // general identity-level location, editable by any user in Settings.
    location: seller.location || 'Nigeria',
    joinDate: seller.member_since
      ? `Since ${new Date(seller.member_since).getFullYear()}`
      : seller.created_at
        ? `Since ${new Date(seller.created_at).getFullYear()}`
        : 'Member',
    verified: seller.verified || false,
    canMessage: seller.can_message !== false,
    isSavedByViewer: Boolean(seller.is_saved_by_viewer),
    isReportedByViewer: Boolean(seller.is_reported_by_viewer),
    company_name: seller.company_name || null,
    business_logo_url: seller.business_logo_url || null,
    social: {
      instagram: seller.instagram_handle || null,
      twitter: seller.twitter_handle || null,
      facebook: seller.facebook_url || null,
      whatsapp: seller.whatsapp_number || null,
    },
    stats: {
      propertiesListed: seller.active_properties_count || 0,
      sold: seller.sold_count || 0,
      responseTime: seller.response_time || 'No replies yet',
      saved: seller.saved_count || 0,
    },
    contact: {
      phone: seller.phone || null,
      email: seller.email || null,
    },
    bio: seller.bio || 'Premium real estate professional.',
    properties: (seller.recent_properties || []).map((prop) => ({
      id: prop.id,
      title: prop.title,
      price: prop.price,
      image: prop.image || (prop.images && prop.images[0]) || placeholderImage,
      beds: prop.beds || prop.bedrooms || 0,
      baths: prop.baths || prop.bathrooms || 0,
      state: prop.state,
      area: prop.area,
    })),
    sellerReviews:
      reviews.map((review) => ({
        id: review.id,
        buyer_id: review.buyer_id || null,
        author: review.buyer_name || 'Anonymous',
        buyer_avatar: review.buyer_avatar || null,
        is_anonymous: review.is_anonymous,
        rating: review.rating || 0,
        date: review.created_at ? new Date(review.created_at).toLocaleDateString() : 'Recently',
        created_at: review.created_at || null,
        is_edited: Boolean(review.edited_at),
        comment: review.comment || '',
        subject: review.subject || 'general',
        title: review.title || null,
        property_title: review.property_title || null,
        property_id: review.property_id || null,
        category_ratings: review.category_ratings || {},
        recommendation: Boolean(review.recommendation),
        review_type: review.review_type || [],
        did_visit: review.did_visit,
        recommendation_choice: review.recommendation_choice || null,
        photos: review.photos || [],
        helpful_count: review.helpful_count || 0,
        current_user_helpful_voted: Boolean(review.current_user_helpful_voted),
        transaction_ref: review.transaction_ref || null,
        seller_response: review.seller_response || '',
        seller_response_at: review.seller_response_at || null,
      })) || [],
  };

  // Contributor data folds directly into the one profile SellerProfileFull
  // already renders (its own header, own Properties/Reviews tab bar) -
  // "Articles" becomes a third option in that same tab bar instead of a
  // second header/tab-bar stacked above it.
  sellerData.isContributor = Boolean(seller.is_contributor);
  sellerData.articles = seller.articles || [];
  sellerData.contributorProfile = seller.contributor_profile || null;

  return (
    <>
      <Breadcrumbs items={breadcrumbItems} />
      <SellerProfileFull seller={sellerData} />
    </>
  );
};

export default SellerProfilePage;
